// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Model,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  crewmemberAccessTokenStatusLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import CrewmemberAccessTokenFields from '../../fields/CrewmemberAccessTokenFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'CrewmemberAccessToken'>>;
  loading: boolean;
  mainItem: $Model<'Crewmember'> | null;
};

const pageId = adminPages.adminCrewmemberAccessTokensPage;

class AdminCrewmemberAccessTokensPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      mainItem: null,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'CrewmemberAccessToken') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      params,
    } = this.props;

    const {
      CrewmemberId,
    } = params;

    await defaultGetData({
      action: async () => {
        if (CrewmemberId) {
          await this.setStateAsync({
            loading: true,
          });

          const mainItem = await Services.Api.getOneAdminCrewmember({
            id: CrewmemberId,
          });
          const data = await Services.Api.getManyAdminCrewmemberAccessToken({
            CrewmemberId,
          });

          await this.setStateAsync({
            data,
            loading: false,
            mainItem,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      redirect,
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          description={mainItem !== null ? mainItem.Name : ''}
          title={t('Admin access tokens')}
          titleButton={(
            <Button
              className={'mr-2'}
              color={'secondary'}
              id={'redirect-admin-crewmembers-page-button'}
              onClick={() => redirect({
                pathname: '/admin/crewmembers',
              })}
              size={'sm'}
            >
              <em className={'fa icon-arrow-left'} />
            </Button>
          )}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'CrewmemberAccessToken'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <CrewmemberAccessTokenFields item={dataItem} />
          )}
          id={`update-crewmember-access-token-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'CrewmemberAccessToken'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.updateAdminCrewmemberAccessToken(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Crewmember access token')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-crewmember-access-token-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-crewmember-access-token-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminCrewmemberAccessToken(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Crewmember access token')}
        >
          <Button
            color={'danger'}
            id={`delete-crewmember-access-token-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Status')}</th>
            <th>{t('Expires')}</th>
            <th>{t('Login date')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'CrewmemberAccessToken'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>{t(crewmemberAccessTokenStatusLabel[dataItem.Status])}</td>
              <TdDate
                countdown={t('Expired')}
                value={dataItem.Exp}
              />
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminCrewmemberAccessTokensPageView;
